(function () {
  'use strict';

  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getScrollStep(section) {
    var firstSlide = section.querySelector('[data-product-short-video-slide]');
    var gap = parseFloat(getComputedStyle(section).getPropertyValue('--product-short-videos-gap')) || 10;
    return firstSlide ? firstSlide.getBoundingClientRect().width + gap : 180;
  }

  function getYouTubeId(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.origin);
      var host = url.hostname.replace(/^www\./, '');
      var path = url.pathname.split('/').filter(Boolean);

      if (host === 'youtu.be') return path[0] || '';
      if (path[0] === 'shorts' || path[0] === 'embed') return path[1] || '';

      return url.searchParams.get('v') || '';
    } catch (error) {
      return '';
    }
  }

  function getTikTokId(rawUrl) {
    var match = String(rawUrl || '').match(/\/video\/(\d+)/);
    return match ? match[1] : '';
  }

  function getInstagramEmbed(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.origin);
      var match = url.pathname.match(/\/(reel|p|tv)\/([^/?#]+)/);
      if (!match) return '';
      return 'https://www.instagram.com/' + match[1] + '/' + match[2] + '/embed';
    } catch (error) {
      return '';
    }
  }

  function isEmbedProvider(provider) {
    return provider === 'youtube' || provider === 'tiktok' || provider === 'instagram';
  }

  function shouldUseNativeMp4Player() {
    return window.innerWidth < 768 ||
      (window.matchMedia && window.matchMedia('(hover: none)').matches) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  }

  function canAutoplayProvider(provider) {
    return provider === 'mp4' || provider === 'youtube' || provider === 'tiktok';
  }

  var QUALITY_ORDER = ['1080', '720', '480'];
  var hlsManifestCache = {};
  var hlsLibraryPromise = null;

  function toPositiveNumber(value) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function inferHeightFromUrl(url) {
    var match = String(url || '').match(/(?:^|[._-])(?:HD-|SD-)?(\d{3,4})p(?:[._-]|$)/i);
    if (!match) match = String(url || '').match(/(\d{3,4})p/i);
    return match ? toPositiveNumber(match[1]) : 0;
  }

  function getQualityFromHeight(height) {
    if (height >= 1000) return '1080';
    if (height >= 700) return '720';
    if (height >= 400) return '480';
    return height ? String(height) : 'original';
  }

  function getQualityLabel(quality) {
    if (quality === 'auto') return 'Auto';
    if (quality === 'original') return 'Original';
    return quality + 'p';
  }

  function absolutizeUrl(url) {
    if (!url) return '';
    if (String(url).indexOf('//') === 0) return window.location.protocol + url;

    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  }

  function getThemeAssetUrl(fileName) {
    var currentScript = document.currentScript;

    if (!currentScript || !currentScript.src) {
      var scripts = document.querySelectorAll('script[src*="product-short-videos"]');
      currentScript = scripts.length ? scripts[scripts.length - 1] : null;
    }

    if (!currentScript || !currentScript.src) return fileName;

    return currentScript.src.replace(/\/assets\/[^/?]+(?:\?.*)?$/i, '/assets/' + fileName);
  }

  function loadHlsLibrary() {
    if (canPlayHlsWithLibrary()) return Promise.resolve(true);
    if (hlsLibraryPromise) return hlsLibraryPromise;

    hlsLibraryPromise = new Promise(function (resolve) {
      var script = document.createElement('script');
      script.src = getThemeAssetUrl('hls.min.js');
      script.async = true;
      script.onload = function () {
        resolve(canPlayHlsWithLibrary());
      };
      script.onerror = function () {
        resolve(false);
      };
      document.head.appendChild(script);
    });

    return hlsLibraryPromise;
  }

  function isHlsUrl(url) {
    return /\.m3u8(?:\?|$)/i.test(String(url || ''));
  }

  function isHlsSource(source) {
    if (!source) return false;

    var type = String(source.type || source.mime_type || '').toLowerCase();
    var format = String(source.format || '').toLowerCase();

    return source.isHls ||
      format === 'm3u8' ||
      type.indexOf('mpegurl') !== -1 ||
      isHlsUrl(source.url);
  }

  function normalizeVideoSource(source) {
    if (!source) return null;

    var url = typeof source === 'string' ? source : source.url;
    if (!url) return null;

    var height = toPositiveNumber(source.height) || inferHeightFromUrl(url);
    var width = toPositiveNumber(source.width);
    var type = String(source.type || source.mime_type || '').toLowerCase();
    var format = String(source.format || '').toLowerCase();

    if (format === 'm3u8' || type.indexOf('mpegurl') !== -1 || isHlsUrl(url)) {
      return null;
    }

    return {
      url: absolutizeUrl(url),
      type: type,
      format: format,
      height: height,
      width: width,
      quality: getQualityFromHeight(height)
    };
  }

  function normalizeHlsMasterSource(source) {
    if (!source) return null;

    var url = typeof source === 'string' ? source : source.url;
    if (!url || !isHlsSource(source)) return null;

    var height = toPositiveNumber(source.height) || inferHeightFromUrl(url);
    var width = toPositiveNumber(source.width);

    return {
      url: absolutizeUrl(url),
      type: 'application/x-mpegURL',
      format: 'm3u8',
      height: height,
      width: width,
      quality: 'auto',
      isHls: true,
      isAuto: true
    };
  }

  function getDerivedHlsUrlFromMp4(url) {
    if (!url) return '';

    var absoluteUrl = absolutizeUrl(url);
    var withoutQuery = absoluteUrl.split('?')[0];
    var query = absoluteUrl.indexOf('?') !== -1 ? absoluteUrl.slice(absoluteUrl.indexOf('?')) : '';
    var match = withoutQuery.match(/\/([^/]+)\.[^/]+\.mp4$/i);

    if (!match) return '';

    return withoutQuery.replace(/\/([^/]+)\.[^/]+\.mp4$/i, '/' + match[1] + '.m3u8') + query;
  }

  function sortSourcesByHeight(sources) {
    return sources.sort(function (a, b) {
      if (!a.height && !b.height) return 0;
      if (!a.height) return -1;
      if (!b.height) return 1;
      return a.height - b.height;
    });
  }

  function mergeVideoSources(sources) {
    var seen = {};
    var merged = [];

    sources.forEach(function (source) {
      if (!source || !source.url || seen[source.url]) return;
      seen[source.url] = true;
      merged.push(source);
    });

    return sortSourcesByHeight(merged);
  }

  function getRawVideoSources(slide) {
    if (!slide) return [];

    var rawSources = [];
    var sourceNode = slide.querySelector('[data-product-short-video-sources]');

    if (sourceNode && sourceNode.textContent) {
      try {
        rawSources = JSON.parse(sourceNode.textContent);
      } catch (error) {
        rawSources = [];
      }
    }

    if (!Array.isArray(rawSources)) rawSources = [];

    if (slide.dataset.videoSrc) {
      rawSources.push({
        url: slide.dataset.videoSrc,
        type: 'video/mp4'
      });
    }

    return rawSources;
  }

  function getSlideVideoSources(slide) {
    return mergeVideoSources(getRawVideoSources(slide).map(normalizeVideoSource));
  }

  function getSlideHlsMasterSource(slide) {
    var rawSources = getRawVideoSources(slide);
    var master = null;

    rawSources.some(function (source) {
      master = normalizeHlsMasterSource(source);
      return !!master;
    });

    if (master) return master;

    var derivedUrl = getDerivedHlsUrlFromMp4(slide && slide.dataset.videoSrc);
    return derivedUrl ? normalizeHlsMasterSource({
      url: derivedUrl,
      type: 'application/x-mpegURL',
      format: 'm3u8'
    }) : null;
  }

  function parseHlsAttributeList(value) {
    var attrs = {};
    var pattern = /([A-Z0-9-]+)=("[^"]*"|[^,]*)/gi;
    var match;

    while ((match = pattern.exec(value || ''))) {
      attrs[match[1].toUpperCase()] = String(match[2] || '').replace(/^"|"$/g, '');
    }

    return attrs;
  }

  function parseHlsManifest(masterSource, manifestText) {
    var lines = String(manifestText || '').split(/\r?\n/);
    var sources = [masterSource];

    lines.forEach(function (line, index) {
      var trimmed = line.trim();
      if (trimmed.indexOf('#EXT-X-STREAM-INF:') !== 0) return;

      var attrs = parseHlsAttributeList(trimmed.slice('#EXT-X-STREAM-INF:'.length));
      var variantPath = '';

      for (var i = index + 1; i < lines.length; i++) {
        variantPath = lines[i].trim();
        if (variantPath && variantPath.charAt(0) !== '#') break;
      }

      if (!variantPath || variantPath.charAt(0) === '#') return;

      var resolution = String(attrs.RESOLUTION || '').match(/(\d+)x(\d+)/);
      var width = resolution ? toPositiveNumber(resolution[1]) : 0;
      var height = resolution ? toPositiveNumber(resolution[2]) : inferHeightFromUrl(variantPath);

      sources.push({
        url: new URL(variantPath, masterSource.url).toString(),
        type: 'application/x-mpegURL',
        format: 'm3u8',
        height: height,
        width: width,
        bandwidth: toPositiveNumber(attrs.BANDWIDTH),
        quality: getQualityFromHeight(height),
        isHls: true
      });
    });

    return mergeVideoSources(sources);
  }

  function getSlideHlsSources(slide) {
    var masterSource = getSlideHlsMasterSource(slide);
    if (!masterSource || !window.fetch) return Promise.resolve([]);

    if (!hlsManifestCache[masterSource.url]) {
      hlsManifestCache[masterSource.url] = fetch(masterSource.url, {
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to load HLS manifest');
        return response.text();
      }).then(function (manifestText) {
        return parseHlsManifest(masterSource, manifestText);
      }).catch(function () {
        return [masterSource];
      });
    }

    return hlsManifestCache[masterSource.url];
  }

  function getSlideVideoSourcesAsync(slide) {
    if (!slide) return Promise.resolve([]);

    if (slide._productShortVideoSourcesPromise) {
      return slide._productShortVideoSourcesPromise;
    }

    var mp4Sources = getSlideVideoSources(slide);
    slide._productShortVideoSourcesPromise = getSlideHlsSources(slide).then(function (hlsSources) {
      return mergeVideoSources(mp4Sources.concat(hlsSources || []));
    });

    return slide._productShortVideoSourcesPromise;
  }

  function getConcreteSources(sources) {
    return (sources || []).filter(function (source) {
      return source && !source.isAuto;
    });
  }

  function getHighestSource(sources) {
    var concreteSources = getConcreteSources(sources);
    return concreteSources.length ? concreteSources[concreteSources.length - 1] : null;
  }

  function findSourceForQuality(sources, quality) {
    if (!sources || !sources.length) return null;
    if (quality === 'auto') {
      return sources.find(function (source) {
        return source && source.isAuto;
      }) || null;
    }

    var target = toPositiveNumber(quality);
    var concreteSources = getConcreteSources(sources);
    if (!target) return getHighestSource(sources);

    var exact = concreteSources.find(function (source) {
      return source.quality === quality || (source.height && Math.abs(source.height - target) <= 80);
    });
    if (exact) return exact;

    var below = null;
    concreteSources.forEach(function (source) {
      if (source.height && source.height <= target) below = source;
    });
    if (below) return below;

    return concreteSources.find(function (source) {
      return source.height && source.height > target;
    }) || getHighestSource(sources);
  }

  function hasConcreteQuality(sources, quality) {
    var target = toPositiveNumber(quality);
    return getConcreteSources(sources).some(function (source) {
      return source.quality === quality || (source.height && Math.abs(source.height - target) <= 80);
    });
  }

  function getAvailableQualityOptions(sources) {
    if (!sources || !sources.length) return [];

    var options = QUALITY_ORDER.filter(function (quality) {
      return hasConcreteQuality(sources, quality);
    }).map(function (quality) {
      return {
        value: quality,
        label: getQualityLabel(quality)
      };
    });

    var hasAutoSource = !!findSourceForQuality(sources, 'auto');
    if (!options.length) return [];

    if (hasAutoSource || options.length > 1) {
      options.unshift({
        value: 'auto',
        label: 'Auto'
      });
    }

    return options.length > 1 ? options : [];
  }

  function getPreviewVideoSource(slide) {
    var sources = getSlideVideoSources(slide);
    return findSourceForQuality(sources, '480') || findSourceForQuality(sources, '720') || getHighestSource(sources);
  }

  function getPreviewVideoSourceAsync(slide) {
    return getSlideVideoSourcesAsync(slide).then(function (sources) {
      return findSourceForQuality(sources, '480') || findSourceForQuality(sources, '720') || getHighestSource(sources);
    });
  }

  function getDefaultModalSource(slide) {
    var sources = getSlideVideoSources(slide);
    return findSourceForQuality(sources, '1080') || getHighestSource(sources);
  }

  function getDefaultModalSourceAsync(slide) {
    return getSlideVideoSourcesAsync(slide).then(function (sources) {
      return findSourceForQuality(sources, '1080') || getHighestSource(sources);
    });
  }

  function getAutoModalSourceFromSources(sources) {
    var hlsAutoSource = findSourceForQuality(sources, 'auto');
    if (hlsAutoSource) return hlsAutoSource;

    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection && connection.saveData) {
      return findSourceForQuality(sources, '480') || getHighestSource(sources);
    }

    if (connection && /(^|-)2g$|slow-2g/i.test(connection.effectiveType || '')) {
      return findSourceForQuality(sources, '480') || getHighestSource(sources);
    }

    if (connection && /3g/i.test(connection.effectiveType || '')) {
      return findSourceForQuality(sources, '720') || getHighestSource(sources);
    }

    var displayHeight = Math.min(window.innerHeight * 0.88, 1080) * (window.devicePixelRatio || 1);
    if (displayHeight <= 820) {
      return findSourceForQuality(sources, '720') || getHighestSource(sources);
    }

    return findSourceForQuality(sources, '1080') || getHighestSource(sources);
  }

  function setVideoSource(video, source, options) {
    if (!video || !source || !source.url) return false;

    options = options || {};

    if (video.dataset.currentSrc === source.url) return true;

    var resumeTime = options.keepTime ? video.currentTime || 0 : 0;
    var shouldPlay = options.play === true || (options.play !== false && !video.paused);
    var useHls = isHlsSource(source);

    if (useHls && !canPlayNativeHls(video) && !canPlayHlsWithLibrary()) {
      loadHlsLibrary().then(function (isReady) {
        if (isReady) {
          setVideoSource(video, source, options);
        }
      });
      return true;
    }

    video.dataset.currentSrc = source.url;
    video.dataset.currentQuality = source.quality || 'original';

    if (resumeTime > 0) {
      video.addEventListener('loadedmetadata', function handleLoadedMetadata() {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        try {
          var maxTime = Number.isFinite(video.duration) ? Math.max(video.duration - 0.3, 0) : resumeTime;
          video.currentTime = Math.min(resumeTime, maxTime);
        } catch (error) {}
      });
    }

    if (useHls && !canPlayNativeHls(video)) {
      destroyVideoHls(video);
      video.removeAttribute('src');

      var hls = new window.Hls();
      video._productShortVideosHls = hls;
      hls.loadSource(source.url);
      hls.attachMedia(video);
    } else {
      destroyVideoHls(video);
      video.src = source.url;
      video.load();
    }

    if (shouldPlay) {
      video.play().catch(function () {});
    }

    return true;
  }

  function setActiveQualityButton(controls, quality) {
    if (!controls) return;

    controls.querySelectorAll('[data-product-short-video-quality]').forEach(function (button) {
      var isActive = button.dataset.productShortVideoQuality === quality;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function canPlayNativeHls(video) {
    if (!video || typeof video.canPlayType !== 'function') return false;

    return !!(
      video.canPlayType('application/vnd.apple.mpegurl') ||
      video.canPlayType('application/x-mpegURL')
    );
  }

  function canPlayHlsWithLibrary() {
    return !!(window.Hls && typeof window.Hls.isSupported === 'function' && window.Hls.isSupported());
  }

  function destroyVideoHls(video) {
    if (!video || !video._productShortVideosHls) return;

    try {
      video._productShortVideosHls.destroy();
    } catch (error) {}

    video._productShortVideosHls = null;
  }

  function buildEmbedUrl(provider, rawUrl, options) {
    options = options || {};
    var shouldAutoplay = options.autoplay !== false;

    if (provider === 'youtube') {
      var youtubeId = getYouTubeId(rawUrl);
      if (!youtubeId) return '';

      var youtubeParams = new URLSearchParams({
        autoplay: shouldAutoplay ? '1' : '0',
        mute: shouldAutoplay ? '1' : '0',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
        iv_load_policy: '3',
        controls: options.controls === false ? '0' : '1',
        fs: options.inline ? '0' : '1'
      });

      return 'https://www.youtube.com/embed/' + youtubeId + '?' + youtubeParams.toString();
    }

    if (provider === 'tiktok') {
      var tiktokId = getTikTokId(rawUrl);
      if (!tiktokId) return '';

      var tiktokParams = new URLSearchParams({
        autoplay: shouldAutoplay ? '1' : '0',
        loop: '1',
        mute: '1',
        muted: '1',
        playsinline: '1',
        controls: '0',
        rel: '0',
        music_info: '0',
        description: '0',
        native_context_menu: '0',
        closed_caption: '0',
        play_button: '0',
        progress_bar: '0',
        volume_control: '0',
        fullscreen_button: '0'
      });

      return 'https://www.tiktok.com/player/v1/' + tiktokId + '?' + tiktokParams.toString();
    }

    if (provider === 'instagram') {
      return getInstagramEmbed(rawUrl);
    }

    return '';
  }

  function ProductShortVideos(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-product-short-videos-viewport]');
    this.track = section.querySelector('[data-product-short-videos-track]');
    this.modal = section.querySelector('[data-product-short-videos-modal]');
    this.modalMedia = section.querySelector('[data-product-short-videos-modal-media]');
    this.prevButton = section.querySelector('[data-product-short-videos-prev]');
    this.nextButton = section.querySelector('[data-product-short-videos-next]');
    this.hoverPlay = section.dataset.hoverPlay === 'true';
    this.mute = section.dataset.mute !== 'false';
    this.videoTitleFallback = section.dataset.videoTitleFallback || 'Video';
    this.resizeTimer = null;
    this.scrollRaf = null;
    this.scrollPlaybackTimer = null;
    this.defaultPlaybackTimer = null;
    this.defaultPlaybackIdle = null;
    this.defaultPlaybackTriggered = false;
    this.manualPlaybackPauseUntil = 0;
    this.defaultPlaybackObserver = null;
    this.lazyObserver = null;
    this.isDestroyed = false;
    this.drag = {
      down: false,
      startX: 0,
      scrollLeft: 0,
      moved: false,
      suppressClick: false
    };

    this.init();
  }

  ProductShortVideos.prototype.init = function () {
    this.updateColumns();
    this.setupCards();
    this.setupControls();
    this.setupDrag();
    this.setupModal();
    this.setupLazyLoading();
    this.setupDefaultPlayback();
    this.updateOverflowState();

    this.handleResize = this.onResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  };

  ProductShortVideos.prototype.onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      self.updateColumns();
      self.updateOverflowState();
      self.scheduleVisiblePlayback(160);
    }, 120);
  };

  ProductShortVideos.prototype.updateColumns = function () {
    var desktopCards = parseFloat(this.section.dataset.desktopCards || '3');
    var mobileCards = parseFloat(this.section.dataset.mobileCards || '1.65');
    var isMobile = window.innerWidth < 768;
    var columns = isMobile ? mobileCards : desktopCards;

    if (!columns || columns < 1) columns = isMobile ? 1.65 : 3;
    if (!isMobile) {
      columns = clamp(columns, 1, 3);
    } else {
      columns = clamp(Math.max(columns, window.innerWidth < 390 ? 1.55 : 1.65), 1.5, 2);
    }

    this.section.style.setProperty('--product-short-videos-columns', columns);
  };

  ProductShortVideos.prototype.updateOverflowState = function () {
    if (!this.viewport || !this.track) return;

    var hasOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
    this.section.classList.toggle('has-overflow', hasOverflow);
    this.track.classList.toggle('is-centered', !hasOverflow);

    var isMobile = window.innerWidth < 768;
    var controls = this.section.querySelector('[data-product-short-videos-controls]');
    if (controls) {
      controls.hidden = !hasOverflow || isMobile;
    }

    if (this.prevButton) {
      this.prevButton.hidden = !hasOverflow || isMobile || this.viewport.scrollLeft <= 2;
    }

    if (this.nextButton) {
      var atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 2;
      this.nextButton.hidden = !hasOverflow || isMobile || atEnd;
    }
  };

  ProductShortVideos.prototype.loadVideo = function (card, options) {
    var video = card && card.querySelector('video');
    if (!video) return null;

    options = options || {};
    if (options.preload) {
      video.preload = options.preload;
    }

    var slide = card.closest('[data-product-short-video-slide]');

    if (!video.getAttribute('src') && video.dataset.src) {
      var previewSource = getPreviewVideoSource(slide) || normalizeVideoSource({
        url: video.dataset.src,
        type: 'video/mp4'
      });

      if (previewSource) {
        setVideoSource(video, previewSource, { play: false });
      } else {
        video.setAttribute('src', video.dataset.src);
        video.load();
      }

      video.removeAttribute('data-src');
    }

    if (slide && !video.dataset.productShortVideoPreviewResolved) {
      video.dataset.productShortVideoPreviewResolved = 'true';
      getPreviewVideoSourceAsync(slide).then(function (previewSource) {
        if (!previewSource || video.dataset.currentSrc === previewSource.url) return;
        if (!document.documentElement.contains(video)) return;

        setVideoSource(video, previewSource, {
          keepTime: true,
          play: !video.paused
        });
      });
    }

    return video;
  };

  ProductShortVideos.prototype.createEmbedIframe = function (provider, rawUrl, title, options) {
    var embedUrl = buildEmbedUrl(provider, rawUrl, options);

    if (!embedUrl) return null;

    var iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.title = title || this.videoTitleFallback || 'Video';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.dataset.provider = provider;
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');

    return iframe;
  };

  ProductShortVideos.prototype.clearInlineEmbeds = function (exceptSlide) {
    this.section.querySelectorAll('[data-product-short-video-inline-frame]').forEach(function (iframe) {
      var slide = iframe.closest('[data-product-short-video-slide]');
      if (exceptSlide && slide === exceptSlide) return;

      var card = slide && slide.querySelector('[data-product-short-video-card]');
      var playButton = card && card.querySelector('.social-video-card__direct-play-button');

      iframe.remove();

      if (card) {
        card.classList.remove('playing', 'is-embed-playing');
      }

      if (playButton) {
        playButton.classList.remove('is-playing');
      }
    });
  };

  ProductShortVideos.prototype.pauseInlineVideos = function (exceptVideo, exceptSlide) {
    this.section.querySelectorAll('.product-short-videos__video').forEach(function (video) {
      if (video === exceptVideo) return;

      video.pause();
      var card = video.closest('[data-product-short-video-card]');
      if (card) {
        card.classList.remove('playing');
        var playButton = card.querySelector('.social-video-card__direct-play-button');
        if (playButton) playButton.classList.remove('is-playing');
      }
    });

    this.clearInlineEmbeds(exceptSlide || null);
  };

  ProductShortVideos.prototype.toggleInlineVideo = function (card, slide) {
    var video = this.loadVideo(card, { preload: 'auto' });
    if (!video) return;
    var wasPaused = video.paused;

    this.pauseInlineVideos(video, slide || null);

    if (wasPaused) {
      this.manualPlaybackPauseUntil = 0;
      video.muted = true;
      video.defaultMuted = true;
      video.play().catch(function () {});
    } else {
      this.manualPlaybackPauseUntil = Date.now() + 1600;
      video.pause();
    }
  };

  ProductShortVideos.prototype.playInlineEmbed = function (slide, options) {
    if (!slide || !isEmbedProvider(slide.dataset.provider)) return false;

    options = options || {};

    var card = slide.querySelector('[data-product-short-video-card]');
    var media = slide.querySelector('.product-short-videos__media');
    var existingIframe = slide.querySelector('[data-product-short-video-inline-frame]');

    if (!card || !media) return false;

    if (existingIframe) {
      if (options.auto) return true;

      this.manualPlaybackPauseUntil = Date.now() + 1600;
      existingIframe.remove();
      card.classList.remove('playing', 'is-embed-playing');
      var currentPlayButton = card.querySelector('.social-video-card__direct-play-button');
      if (currentPlayButton) currentPlayButton.classList.remove('is-playing');
      return false;
    }

    var provider = slide.dataset.provider;
    var rawUrl = slide.dataset.videoUrl || '';
    var title = slide.dataset.videoTitle || this.videoTitleFallback || 'Video';
    var iframe = this.createEmbedIframe(provider, rawUrl, title, {
      inline: true,
      autoplay: true,
      controls: provider !== 'tiktok'
    });

    if (!iframe) return false;

    iframe.className = 'product-short-videos__inline-frame';
    iframe.setAttribute('data-product-short-video-inline-frame', '');

    if (!options.auto) this.manualPlaybackPauseUntil = 0;
    this.pauseInlineVideos(null, slide);
    media.appendChild(iframe);

    card.classList.add('playing', 'is-embed-playing');
    var playButton = card.querySelector('.social-video-card__direct-play-button');
    if (playButton) playButton.classList.add('is-playing');

    return true;
  };

  ProductShortVideos.prototype.playSlideInline = function (slide, options) {
    if (!slide) return false;

    options = options || {};
    if (options.auto && Date.now() < this.manualPlaybackPauseUntil) return false;

    var provider = slide.dataset.provider;
    var card = slide.querySelector('[data-product-short-video-card]');

    if (provider === 'mp4') {
      var video = this.loadVideo(card, { preload: 'auto' });
      if (!video) return false;

      video.muted = true;
      video.defaultMuted = true;
      this.pauseInlineVideos(video, slide);
      this.defaultPlaybackTriggered = true;
      video.play().catch(function () {});

      return true;
    }

    if (isEmbedProvider(provider)) {
      return this.playInlineEmbed(slide, options);
    }

    return false;
  };

  ProductShortVideos.prototype.openNativeMp4Player = function (slide, sourceVideo) {
    if (!slide) return false;

    var card = slide.querySelector('[data-product-short-video-card]');
    var video = card ? this.loadVideo(card, { preload: 'auto' }) : sourceVideo;
    if (!video) return false;

    this.closeModal();
    this.pauseInlineVideos(video, slide);
    this.clearInlineEmbeds(slide);

    var hadControls = video.controls;
    video.controls = true;
    video.preload = 'auto';
    video.muted = this.mute;
    video.defaultMuted = this.mute;

    var restoreControls = function () {
      video.controls = hadControls;
      video.removeEventListener('webkitendfullscreen', restoreControls);
      document.removeEventListener('fullscreenchange', restoreControls);
      document.removeEventListener('webkitfullscreenchange', restoreControls);
    };

    video.addEventListener('webkitendfullscreen', restoreControls);
    document.addEventListener('fullscreenchange', restoreControls);
    document.addEventListener('webkitfullscreenchange', restoreControls);

    var enteredNativePlayer = false;

    try {
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
        enteredNativePlayer = true;
      } else if (typeof video.webkitEnterFullScreen === 'function') {
        video.webkitEnterFullScreen();
        enteredNativePlayer = true;
      } else if (typeof video.requestFullscreen === 'function') {
        video.requestFullscreen().catch(function () {});
        enteredNativePlayer = true;
      } else if (typeof video.webkitRequestFullscreen === 'function') {
        video.webkitRequestFullscreen();
        enteredNativePlayer = true;
      }
    } catch (error) {
      enteredNativePlayer = false;
    }

    if (!enteredNativePlayer) {
      restoreControls();
      return false;
    }

    video.play().catch(function () {});
    return true;
  };

  ProductShortVideos.prototype.openModal = function (slide, sourceVideo) {
    if (!this.modal || !this.modalMedia || !slide) return;

    var provider = slide.dataset.provider;
    var videoSrc = slide.dataset.videoSrc || '';
    var rawUrl = slide.dataset.videoUrl || '';
    var title = slide.dataset.videoTitle || this.videoTitleFallback || 'Video';

    if (provider === 'mp4' &&
        shouldUseNativeMp4Player() &&
        !getSlideHlsMasterSource(slide) &&
        !getAvailableQualityOptions(getSlideVideoSources(slide)).length &&
        this.openNativeMp4Player(slide, sourceVideo)) {
      return;
    }

    this.pauseInlineVideos(sourceVideo || null);
    this.modalMedia.innerHTML = '';

    if (provider === 'mp4' && videoSrc) {
      var modalSource = getDefaultModalSource(slide) || normalizeVideoSource({
        url: sourceVideo && (sourceVideo.currentSrc || sourceVideo.src) ? sourceVideo.currentSrc || sourceVideo.src : videoSrc,
        type: 'video/mp4'
      });

      if (!modalSource) return;

      var video = document.createElement('video');
      video.controls = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.muted = this.mute;
      video.preload = 'auto';
      this.modalMedia.appendChild(video);
      setVideoSource(video, modalSource, { play: true });
      this.renderQualityControls(slide, video, modalSource.quality);
    } else {
      var iframe = this.createEmbedIframe(provider, rawUrl, title, {
        inline: false,
        autoplay: true,
        controls: provider !== 'tiktok'
      });

      if (!iframe) return;

      this.modalMedia.appendChild(iframe);
    }

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    if (this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }
    document.body.style.overflow = 'hidden';

    if (sourceVideo) {
      sourceVideo.pause();
    }
  };

  ProductShortVideos.prototype.renderQualityControls = function (slide, video, currentQuality) {
    if (!this.modalMedia || !slide || !video) return;

    var self = this;
    var renderControls = function (sources) {
      if (!self.modalMedia || !document.documentElement.contains(video)) return;

      var existingControls = self.modalMedia.querySelector('.product-short-videos__modal-quality');
      if (existingControls) existingControls.remove();

      var options = getAvailableQualityOptions(sources);
      if (!options.length) return;

      var controls = document.createElement('div');
      controls.className = 'product-short-videos__modal-quality';
      controls.setAttribute('aria-label', 'Video quality');

      options.forEach(function (option) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-short-videos__modal-quality-button';
        button.dataset.productShortVideoQuality = option.value;
        button.textContent = option.label;
        button.setAttribute('aria-pressed', 'false');

        button.addEventListener('click', function () {
          var selectedQuality = button.dataset.productShortVideoQuality;
          var selectedSource = selectedQuality === 'auto'
            ? getAutoModalSourceFromSources(sources)
            : findSourceForQuality(sources, selectedQuality);

          if (!selectedSource) return;

          if (setVideoSource(video, selectedSource, {
            keepTime: true,
            play: true
          })) {
            setActiveQualityButton(controls, selectedQuality);
          }
        });

        controls.appendChild(button);
      });

      self.modalMedia.appendChild(controls);
      setActiveQualityButton(controls, currentQuality || video.dataset.currentQuality || '1080');
    };

    renderControls(getSlideVideoSources(slide));
    getSlideVideoSourcesAsync(slide).then(renderControls);
  };

  ProductShortVideos.prototype.closeModal = function () {
    if (!this.modal || !this.modalMedia) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modalMedia.querySelectorAll('video').forEach(destroyVideoHls);
    this.modalMedia.innerHTML = '';
    document.body.style.overflow = '';
  };

  ProductShortVideos.prototype.setupCards = function () {
    var self = this;

    this.section.querySelectorAll('[data-product-short-video-card]').forEach(function (card) {
      var slide = card.closest('[data-product-short-video-slide]');
      var provider = slide ? slide.dataset.provider : '';
      var fullscreenButton = card.querySelector('[data-product-short-video-fullscreen]');
      var playButton = card.querySelector('.social-video-card__direct-play-button');
      var video = card.querySelector('video');

      card.addEventListener('click', function (event) {
        if (event.target.closest('a') || event.target.closest('[data-product-short-video-fullscreen]')) return;
        if (!slide) return;

        event.preventDefault();

        if (provider === 'mp4') {
          self.toggleInlineVideo(card, slide);
        } else if (isEmbedProvider(provider)) {
          self.playInlineEmbed(slide);
        } else {
          self.openModal(slide, video || null);
        }
      });

      card.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        if (provider === 'mp4') {
          self.toggleInlineVideo(card, slide);
        } else if (isEmbedProvider(provider)) {
          self.playInlineEmbed(slide);
        } else {
          self.openModal(slide, video || null);
        }
      });

      if (fullscreenButton) {
        fullscreenButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.openModal(slide, video || null);
        });
      }

      if (video) {
        video.addEventListener('play', function () {
          card.classList.add('playing');
          if (playButton) playButton.classList.add('is-playing');
        });

        video.addEventListener('pause', function () {
          card.classList.remove('playing');
          if (playButton) playButton.classList.remove('is-playing');
        });

        video.addEventListener('ended', function () {
          card.classList.remove('playing');
          if (playButton) playButton.classList.remove('is-playing');
        });
      }

      if (self.hoverPlay && provider === 'mp4') {
        card.addEventListener('mouseenter', function () {
          if (window.innerWidth < 768) return;
          var loadedVideo = self.loadVideo(card, { preload: 'auto' });
          if (!loadedVideo) return;

          self.manualPlaybackPauseUntil = 0;
          loadedVideo.muted = true;
          loadedVideo.defaultMuted = true;
          self.pauseInlineVideos(loadedVideo, slide);
          loadedVideo.play().catch(function () {});
        });
      }
    });
  };

  ProductShortVideos.prototype.setupControls = function () {
    var self = this;
    if (!this.viewport) return;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.viewport.scrollBy({ left: -getScrollStep(self.section), behavior: 'smooth' });
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.viewport.scrollBy({ left: getScrollStep(self.section), behavior: 'smooth' });
      });
    }

    this.viewport.addEventListener('scroll', function () {
      if (!self.scrollRaf) {
        self.scrollRaf = window.requestAnimationFrame(function () {
          self.scrollRaf = null;
          self.updateOverflowState();
        });
      }

      self.scheduleVisiblePlayback();
    }, { passive: true });

    this.viewport.addEventListener('touchend', function () {
      self.scheduleVisiblePlayback(220);
    }, { passive: true });

    this.viewport.addEventListener('scrollend', function () {
      self.scheduleVisiblePlayback(40);
    }, { passive: true });
  };

  ProductShortVideos.prototype.scheduleVisiblePlayback = function (delay) {
    var self = this;

    clearTimeout(this.scrollPlaybackTimer);
    this.scrollPlaybackTimer = window.setTimeout(function () {
      self.scrollPlaybackTimer = null;
      if (Date.now() < self.manualPlaybackPauseUntil) return;
      self.playMostVisibleVideo();
    }, typeof delay === 'number' ? delay : 180);
  };

  ProductShortVideos.prototype.playMostVisibleVideo = function () {
    if (!this.viewport) return false;
    if (Date.now() < this.manualPlaybackPauseUntil) return false;

    var viewportRect = this.viewport.getBoundingClientRect();
    var bestSlide = null;
    var bestRatio = 0;

    this.section.querySelectorAll('[data-product-short-video-slide]').forEach(function (slide) {
      if (!canAutoplayProvider(slide.dataset.provider)) return;

      var rect = slide.getBoundingClientRect();
      var visibleWidth = Math.max(0, Math.min(rect.right, viewportRect.right) - Math.max(rect.left, viewportRect.left));
      var visibleHeight = Math.max(0, Math.min(rect.bottom, viewportRect.bottom) - Math.max(rect.top, viewportRect.top));
      var area = Math.max(1, rect.width * rect.height);
      var ratio = (visibleWidth * visibleHeight) / area;

      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestSlide = slide;
      }
    });

    if (!bestSlide || bestRatio < 0.35) return false;

    return this.playSlideInline(bestSlide, { auto: true });
  };

  ProductShortVideos.prototype.scheduleDefaultPlayback = function (delay) {
    var self = this;

    if (this.defaultPlaybackTriggered) return;

    clearTimeout(this.defaultPlaybackTimer);
    if (this.defaultPlaybackIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.defaultPlaybackIdle);
      this.defaultPlaybackIdle = null;
    }

    this.defaultPlaybackTimer = window.setTimeout(function () {
      self.defaultPlaybackTimer = null;
      if (self.isDestroyed || self.defaultPlaybackTriggered) return;

      if ('requestIdleCallback' in window) {
        self.defaultPlaybackIdle = window.requestIdleCallback(function () {
          self.defaultPlaybackIdle = null;
          if (!self.isDestroyed) self.playFirstVideo();
        }, { timeout: 1200 });
      } else {
        self.playFirstVideo();
      }
    }, typeof delay === 'number' ? delay : 500);
  };

  ProductShortVideos.prototype.setupDrag = function () {
    var self = this;
    var viewport = this.viewport;
    if (!viewport) return;

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;

      self.drag.down = true;
      self.drag.startX = event.clientX;
      self.drag.scrollLeft = viewport.scrollLeft;
      self.drag.moved = false;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (event.pointerType === 'touch') return;
      if (!self.drag.down) return;

      var delta = event.clientX - self.drag.startX;
      if (Math.abs(delta) > 4) {
        self.drag.moved = true;
        viewport.classList.add('is-dragging');
        viewport.scrollLeft = self.drag.scrollLeft - delta;
        event.preventDefault();
      }
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (eventName) {
      viewport.addEventListener(eventName, function () {
        if (!self.drag.down) return;

        self.drag.down = false;
        viewport.classList.remove('is-dragging');
        self.drag.suppressClick = self.drag.moved;
        if (self.drag.moved) self.scheduleVisiblePlayback(80);
        self.drag.moved = false;
      });
    });

    viewport.addEventListener('click', function (event) {
      if (!self.drag.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      self.drag.suppressClick = false;
    }, true);
  };

  ProductShortVideos.prototype.setupLazyLoading = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) return;

    this.lazyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var card = entry.target.querySelector('[data-product-short-video-card]');
        if (card) self.loadVideo(card, { preload: 'metadata' });
        self.lazyObserver.unobserve(entry.target);
      });
    }, {
      root: this.viewport || null,
      rootMargin: '0px 80px',
      threshold: 0.01
    });

    this.section.querySelectorAll('[data-provider="mp4"]').forEach(function (slide) {
      self.lazyObserver.observe(slide);
    });
  };

  ProductShortVideos.prototype.setupDefaultPlayback = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      this.scheduleDefaultPlayback(600);
      return;
    }

    this.defaultPlaybackObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        self.scheduleDefaultPlayback(500);
        if (self.defaultPlaybackObserver) self.defaultPlaybackObserver.unobserve(self.section);
      });
    }, {
      threshold: 0.25
    });

    this.defaultPlaybackObserver.observe(this.section);
  };

  ProductShortVideos.prototype.playFirstVideo = function () {
    if (this.defaultPlaybackTriggered) return false;

    var firstSlide = this.section.querySelector('[data-product-short-video-slide]');
    if (!firstSlide || !canAutoplayProvider(firstSlide.dataset.provider)) return false;

    return this.playSlideInline(firstSlide, { auto: true });
  };

  ProductShortVideos.prototype.setupModal = function () {
    var self = this;
    var closeButton = this.section.querySelector('[data-product-short-videos-close]');

    if (!this.modal) return;

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        self.closeModal();
      });
    }

    this.modal.addEventListener('click', function (event) {
      if (event.target === self.modal) {
        self.closeModal();
      }
    });

    this.handleEscape = function (event) {
      if (event.key === 'Escape' && self.modal.classList.contains('is-open')) {
        self.closeModal();
      }
    };

    document.addEventListener('keydown', this.handleEscape);
  };

  ProductShortVideos.prototype.destroy = function () {
    this.isDestroyed = true;
    this.clearInlineEmbeds(null);
    window.removeEventListener('resize', this.handleResize);
    if (this.handleEscape) document.removeEventListener('keydown', this.handleEscape);
    if (this.defaultPlaybackObserver) this.defaultPlaybackObserver.disconnect();
    if (this.lazyObserver) this.lazyObserver.disconnect();
    if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.scrollPlaybackTimer);
    clearTimeout(this.defaultPlaybackTimer);
    if (this.defaultPlaybackIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.defaultPlaybackIdle);
    }
  };

  function initAll() {
    document.querySelectorAll('[data-product-short-videos]').forEach(function (section) {
      if (!instances.has(section)) {
        instances.set(section, new ProductShortVideos(section));
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target && event.target.querySelector && event.target.querySelector('[data-product-short-videos]');
    if (!section || !instances.has(section)) return;

    instances.get(section).destroy();
    instances.delete(section);
  });
})();
